import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Coffee } from 'lucide-react';

import {
  AWAY_REASONS,
  AWAY_MAX_MINUTES,
  AWAY_NOTE_MAX,
} from '../../../lib/awayReasons';

// Step away (Nic 2026-10-05): a Sales Rep pauses their own clocks for a short
// break, and new leads skip them. One click picks the reason (required);
// Other asks for a short note (40 characters, never a story). It ends by
// itself after 15 minutes, or at "I'm back" on the away banner. Hidden while
// they are already away (the banner takes over). Disabled while they are on
// a call (Nic 2026-11-02): the call already pauses them.
const StepAwayControl = ({
  onAway,
  busy = false,
  away = false,
  onCall = false,
}) => {
  const [open, setOpen] = useState(false);
  const [other, setOther] = useState(null); // null = list, string = note
  // Which edge the menu lines up with (Nic 2026-10-20: on a phone the button
  // sits near the left edge, and a right-aligned menu ran off the screen).
  const [alignLeft, setAlignLeft] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) setOther(null);
  }, [open]);
  const go = (reason, note) => {
    setOpen(false);
    onAway({ reason, note });
  };
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (away) return null;
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={busy || onCall}
        onClick={() => {
          if (!open && ref.current) {
            const MENU_W = 224; // w-56
            setAlignLeft(ref.current.getBoundingClientRect().right - MENU_W < 8);
          }
          setOpen((o) => !o);
        }}
        title={
          onCall
            ? 'You are on a call. Step away once it ends.'
            : `Pause your clocks for up to ${AWAY_MAX_MINUTES} minutes. New leads skip you while you're away.`
        }
        className="flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-[11px] font-medium text-slate-500 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] transition-[color] hover:text-slate-700 disabled:opacity-50 dark:!bg-surface-card dark:text-slate-400 dark:ring-1 dark:ring-white/10 dark:hover:text-slate-200"
      >
        <Coffee size={13} />
        <span className="os-act-label hidden sm:inline">Step away</span>
        <ChevronDown size={12} />
      </button>
      {open ? (
        <div className={`absolute ${alignLeft ? 'left-0' : 'right-0'} z-30 mt-1.5 w-56 max-w-[calc(100vw-16px)] rounded-xl bg-white p-1.5 shadow-lg ring-1 ring-slate-200 dark:!bg-surface-elevated dark:ring-white/10`}>
          <div className="px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
            Away for up to {AWAY_MAX_MINUTES} min
          </div>
          {other === null ? (
            AWAY_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                disabled={busy}
                onClick={() => (r === 'Other' ? setOther('') : go(r))}
                className="block w-full rounded-lg px-2.5 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:text-slate-200 dark:hover:!bg-white/10"
              >
                {r}
              </button>
            ))
          ) : (
            <div className="px-1 pb-1">
              <input
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                value={other}
                onChange={(e) => setOther(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && other.trim()) go('Other', other.trim());
                }}
                maxLength={AWAY_NOTE_MAX}
                placeholder="What's the reason?"
                className="mt-1 w-full rounded-lg bg-white px-3 py-2 text-[13px] text-slate-700 outline-none ring-1 ring-slate-200 focus:outline-none focus:ring-slate-300 dark:!bg-white/5 dark:text-slate-100 dark:ring-white/10"
              />
              <div className="mt-1.5 flex items-center justify-between gap-2">
                <span className="text-[11px] tabular-nums text-slate-400 dark:text-slate-500">
                  {other.length}/{AWAY_NOTE_MAX}
                </span>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => setOther(null)}
                    className="rounded-lg px-2.5 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:!bg-white/10"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    disabled={busy || !other.trim()}
                    onClick={() => go('Other', other.trim())}
                    className="rounded-lg bg-sky-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-sky-500 disabled:opacity-40"
                  >
                    Step away
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default StepAwayControl;
